// nettiExpression — netti's body language, named.
//
// WHY THIS IS AN ANIMATION ENGINE AND NOT A LIST OF SETTIMEOUTS (the 2026-08-31
// rebuild): the first pass set a tuner, waited, then restored — and several
// expressions visibly did nothing. Three reasons, all fixed here:
//
//   1. The shader eases every tuner on a ~0.62s time constant. A 900ms gesture
//      that sets a dial and hands it straight back never arrives — the organism
//      is still travelling toward a target that has already been withdrawn. So
//      an expression now TWEENS ITS OWN ENVELOPE every frame and holds the peak
//      long enough to be reached.
//   2. The deltas were too small and on the wrong dials. Lighting alone
//      (innerLight/glass) is the subtlest channel there is. Every expression now
//      has a dominant, legible channel — silhouette, size, formation, colour
//      spread — and moves it far.
//   3. `shrink` drove age from 1.0 downward, but natural is ~0.62 and the
//      renderer clamps at 0.92 — so it GREW and came back. Age now moves
//      relative to natural, and only ever DOWNWARD, which is both the fix and
//      the law (size means how much memory exists; see MemoryOrganism's law of
//      the net's natural size).
//
// Every expression is still a round trip: it borrows the body for a beat and
// always hands every dial back (`identity: true` + age released). Nothing that
// moves netti for a moment may keep it. Tuned live at /test/netti-voice.

import { FUTURE_TUNER_DEFAULTS } from "../organism/future/futureOrganism";
import { playNettiSound, type NettiSoundName } from "./sound";

export type DriveDetail = {
  color?: unknown;
  bg?: [number, number, number];
  rotation?: number;
  veins?: number;
  glass?: number;
  cell?: number;
  tuners?: Record<string, number>;
  identity?: true;
  focus?: { angle: number; dist: number; zoom?: number } | null;
  fire?: number | true | "soft";
};

export function driveNetti(detail: DriveDetail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("innernet:organism-drive", { detail }));
}

export function ageNetti(age: number | null) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("innernet:organism-age", { detail: { age } }));
}

/** The size netti sits at when nothing is happening to it. */
export const NATURAL_AGE = 0.62;

type TunerKey = keyof typeof FUTURE_TUNER_DEFAULTS;

export type Expression = {
  name: string;
  label: string;
  hint: string;
  /** Total duration, ms. Long enough for the 0.62s tuner ease to arrive. */
  ms: number;
  /** Absolute tuner values at the peak of the envelope. */
  peak: Partial<Record<TunerKey, number>>;
  /** Age at the peak, as a DROP below natural (0 = no size change). */
  ageDrop?: number;
  /** "hold" eases in, stays, eases out. "bell" goes there and back. */
  curve?: "hold" | "bell";
  /** Neuron streams: normalised time 0..1, and how hard. */
  fires?: { at: number; power?: number | "soft" }[];
  sound?: NettiSoundName;
};

// Each expression leads with ONE unmistakable channel, so they never read alike.
export const NETTI_EXPRESSIONS: Expression[] = [
  {
    name: "wake",
    label: "wake",
    hint: "draws a breath in, then lights up",
    ms: 2200,
    curve: "hold",
    // pulls in a little as it lights — an inhale, not a flinch
    ageDrop: 0.1,
    peak: { innerLight: 1, glass: 0.88, motes: 0.85, moteSize: 0.52, venation: 0.72, nodes: 0.68, cellSize: 0.4 },
    fires: [{ at: 0.04, power: 1 }, { at: 0.3, power: "soft" }, { at: 0.52, power: "soft" }],
    sound: "hello",
  },
  {
    name: "glow",
    label: "glow",
    hint: "becomes a lantern — lit, glassy, colour fanned",
    ms: 2000,
    curve: "hold",
    peak: { innerLight: 1, glass: 0.92, hueSpread: 0.88, venation: 0.78, nodes: 0.72, motes: 0.66 },
    sound: "radiate",
  },
  {
    name: "swell",
    label: "swell",
    hint: "the write-pulse — a felt heartbeat",
    ms: 1800,
    curve: "bell",
    peak: { breath: 0.85, petalLength: 0.74, petalWidth: 0.66, cellSize: 0.46, innerLight: 0.85 },
    fires: [{ at: 0, power: 1.3 }, { at: 0.34, power: "soft" }],
    sound: "notice",
  },
  {
    name: "shrink",
    label: "shrink",
    hint: "shy — genuinely pulls in small, then returns",
    ms: 2000,
    curve: "bell",
    // the big one: a third of its size away, plus the silhouette closing
    ageDrop: 0.34,
    peak: { petalLength: 0.26, petalWidth: 0.3, abundance: 0.34, cellSize: 0.12, motes: 0.14, innerLight: 0.38, glass: 0.4 },
    sound: "absorb",
  },
  {
    name: "radiate",
    label: "radiate",
    hint: "full light — motes rise, colour fans, neurons fire",
    ms: 2400,
    curve: "hold",
    peak: { innerLight: 1, motes: 0.96, moteSize: 0.62, hueSpread: 0.96, venation: 0.86, nodes: 0.8, glass: 0.86 },
    fires: [{ at: 0.03, power: 1.4 }, { at: 0.24, power: "soft" }, { at: 0.44, power: "soft" }, { at: 0.64, power: "soft" }],
    sound: "radiate",
  },
  {
    name: "spin",
    label: "spin",
    hint: "a real turn of delight",
    ms: 2200,
    curve: "hold",
    peak: { revolve: 0.96, sway: 0.72, tempo: 0.78, bend: 0.62, variety: 0.66 },
    sound: "touch",
  },
  {
    name: "think",
    label: "think",
    hint: "slows, dims, and morphs toward a drifting form",
    ms: 2600,
    curve: "hold",
    ageDrop: 0.08,
    // formation is the most dramatic dial there is — thinking visibly re-forms
    peak: { formation: 0.3, tempo: 0.14, sway: 0.74, motes: 0.82, moteSize: 0.22, innerLight: 0.38, glass: 0.42, venation: 0.26 },
    sound: "think",
  },
  {
    name: "nod",
    label: "nod",
    hint: "yes — a small bob, twice",
    ms: 1400,
    curve: "bell",
    ageDrop: 0.07,
    peak: { cellSize: 0.46, petalLength: 0.62, innerLight: 0.8 },
    fires: [{ at: 0.06, power: "soft" }, { at: 0.34, power: "soft" }],
    sound: "touch",
  },
  {
    name: "delight",
    label: "delight",
    hint: "colour fans wide, petals sharpen, everything fires",
    ms: 2200,
    curve: "hold",
    peak: { hueSpread: 1, variety: 0.86, abundance: 0.82, petalLength: 0.76, tipSharpness: 0.72, motes: 0.9, innerLight: 0.96 },
    fires: [{ at: 0.03, power: 1.2 }, { at: 0.26, power: "soft" }, { at: 0.5, power: "soft" }],
    sound: "done",
  },
  {
    name: "rest",
    label: "rest",
    hint: "settles down and dims — off duty",
    ms: 2600,
    curve: "hold",
    ageDrop: 0.2,
    peak: { breath: 0.14, motes: 0.12, moteSize: 0.2, innerLight: 0.3, tempo: 0.2, sway: 0.24, glass: 0.4, revolve: 0 },
    sound: "absorb",
  },
];

export type ExpressionName = (typeof NETTI_EXPRESSIONS)[number]["name"];

const byName = new Map(NETTI_EXPRESSIONS.map((e) => [e.name, e]));

const smoothstep = (x: number) => x * x * (3 - 2 * x);

/** Envelope shape at normalised time p. Both start and end at 0. */
function envelopeAt(p: number, curve: "hold" | "bell"): number {
  if (p <= 0 || p >= 1) return 0;
  if (curve === "bell") return Math.sin(p * Math.PI) ** 0.75;
  // hold — in over the first fifth, full until 70%, out over the tail
  if (p < 0.2) return smoothstep(p / 0.2);
  if (p < 0.7) return 1;
  return smoothstep(1 - (p - 0.7) / 0.3);
}

let activeStop: (() => void) | null = null;

/**
 * Perform an expression on whatever organism is listening to the drive bus.
 * Returns a cancel function that also hands the body straight back.
 */
export function performExpression(
  name: ExpressionName | string,
  opts?: { intensity?: number; sound?: boolean; natural?: number }
): (() => void) | null {
  const def = byName.get(name);
  if (!def || typeof window === "undefined") return null;

  // only one expression owns the body at a time
  activeStop?.();

  const k = Math.min(1, Math.max(0, opts?.intensity ?? 0.6));
  const natural = opts?.natural ?? NATURAL_AGE;
  const withSound = opts?.sound !== false;
  const keys = Object.keys(def.peak) as TunerKey[];
  const fires = (def.fires ?? []).map((f) => ({ ...f, done: false }));

  if (withSound && def.sound) playNettiSound(def.sound);

  const t0 = performance.now();
  let raf = 0;
  let stopped = false;

  const finish = () => {
    if (stopped) return;
    stopped = true;
    cancelAnimationFrame(raf);
    if (activeStop === stop) activeStop = null;
    ageNetti(null);
    driveNetti({ identity: true });
  };

  const frame = () => {
    if (stopped) return;
    const p = (performance.now() - t0) / def.ms;
    if (p >= 1) {
      finish();
      return;
    }
    raf = requestAnimationFrame(frame);
    const env = envelopeAt(p, def.curve ?? "hold") * k;

    // tuners travel from their resting value toward the peak and back
    const tuners: Record<string, number> = {};
    for (const key of keys) {
      const base = FUTURE_TUNER_DEFAULTS[key];
      const target = def.peak[key]!;
      tuners[key] = base + (target - base) * env;
    }
    driveNetti({ tuners });

    // size only ever moves DOWN from natural — the law
    if (def.ageDrop) ageNetti(Math.max(0.12, natural - def.ageDrop * env));

    for (const f of fires) {
      if (!f.done && p >= f.at) {
        f.done = true;
        const power = f.power ?? "soft";
        driveNetti({ fire: power === "soft" ? "soft" : (power as number) * k });
      }
    }
  };

  const stop = () => finish();
  activeStop = stop;
  raf = requestAnimationFrame(frame);
  return stop;
}
